<template>
    <v-menu v-model="menu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="310">
        <template v-slot:activator="{ on, attrs }">
            <v-text-field
                :value="displayValue"
                :label="label"
                append-icon="mdi-calendar-clock"
                readonly
                :outlined="outlined"
                :dense="dense"
                :hide-details="hideDetails"
                :class="inputClass"
                v-bind="attrs"
                v-on="on"
                @click:append="on.click"
            ></v-text-field>
        </template>

        <v-card class="app-dtp-card" style="width:310px; overflow:visible">
            <!-- 오늘 버튼 (우상단) -->
            <div class="app-dtp-today-row">
                <v-btn text x-small class="app-dtp-today-btn" @click="setToday">오늘</v-btn>
            </div>

            <!-- 달력 + 분 오버레이 -->
            <div style="position:relative">
                <v-date-picker
                    v-model="innerDate"
                    locale="ko"
                    :day-format="(d) => parseInt(d.slice(-2))"
                    no-title
                    scrollable
                    full-width
                    elevation="0"
                    class="app-dtp-datepicker"
                    @click:date="onDateSelected"
                ></v-date-picker>

                <!-- 분 선택 드롭다운 오버레이 -->
                <div v-if="minuteOpen" class="app-dtp-minute-overlay" @click.stop>
                    <div
                        v-for="m in minuteOptions"
                        :key="m"
                        class="app-dtp-minute-item"
                        :class="{ 'app-dtp-minute-item--active': innerMinute === m }"
                        @click="selectMinute(m)"
                    >
                        {{ String(m).padStart(2, '0') }}분
                    </div>
                </div>
            </div>

            <!-- 시간 선택 행 -->
            <div class="app-dtp-time-row">
                <select v-model="innerAmpm" class="app-dtp-select" @change="minuteOpen = false">
                    <option value="am">오전</option>
                    <option value="pm">오후</option>
                </select>
                <select v-model="innerHour12" class="app-dtp-select" @change="minuteOpen = false">
                    <option v-for="h in hours12" :key="h" :value="h">{{ h }}시</option>
                </select>
                <div class="app-dtp-minute-trigger" @click="minuteOpen = !minuteOpen">
                    <span>{{ String(innerMinute).padStart(2, '0') }}분</span>
                    <v-icon small>{{ minuteOpen ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </div>
            </div>

            <!-- 확인 버튼 -->
            <div class="app-dtp-confirm-row">
                <v-btn color="primary" block depressed large @click="confirm">확인</v-btn>
            </div>
        </v-card>
    </v-menu>
</template>

<script>
export default {
    name: 'AppDateTimePicker',
    props: {
        value:       { type: String,  default: '' },
        label:       { type: String,  default: '날짜·시간' },
        outlined:    { type: Boolean, default: true },
        dense:       { type: Boolean, default: false },
        hideDetails: { type: Boolean, default: true },
        inputClass:  { type: String,  default: '' },
    },
    data() {
        const parsed = this.parseValue(this.value);
        return {
            menu:        false,
            innerDate:   parsed.date,
            innerAmpm:   parsed.ampm,
            innerHour12: parsed.hour12,
            innerMinute: parsed.minute,
            minuteOpen:  false,
        };
    },
    computed: {
        minuteOptions() {
            return [0, 10, 20, 30, 40, 50];
        },
        hours12() {
            return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
        },
        displayValue() {
            if (!this.value) return '';
            const { date, ampm, hour12, minute } = this.parseValue(this.value);
            if (!date) return '';
            const ampmStr = ampm === 'pm' ? '오후' : '오전';
            return `${date}  ${ampmStr} ${hour12}시 ${String(minute).padStart(2, '0')}분`;
        },
    },
    watch: {
        value(val) {
            const p = this.parseValue(val);
            if (p.date) {
                this.innerDate   = p.date;
                this.innerAmpm   = p.ampm;
                this.innerHour12 = p.hour12;
                this.innerMinute = p.minute;
            }
        },
        menu(open) {
            if (open) {
                const p = this.parseValue(this.value);
                this.innerDate   = p.date   || this.todayStr();
                this.innerAmpm   = p.ampm   || this.nowAmpm();
                this.innerHour12 = p.hour12 || this.nowHour12();
                this.innerMinute = p.date   ? p.minute : this.nowMinute();
                this.minuteOpen  = false;
            }
        },
    },
    methods: {
        parseValue(val) {
            if (!val) return { date: '', ampm: this.nowAmpm(), hour12: this.nowHour12(), minute: this.nowMinute() };
            const str   = val.replace('T', ' ');
            const parts = str.split(' ');
            const date  = parts[0] || '';
            const timeStr = (parts[1] || '').substring(0, 5);
            if (!timeStr) return { date, ampm: 'am', hour12: 12, minute: 0 };
            const [hStr, mStr] = timeStr.split(':');
            const h24    = parseInt(hStr, 10) || 0;
            const m      = Math.floor((parseInt(mStr || '0', 10)) / 10) * 10;
            const ampm   = h24 >= 12 ? 'pm' : 'am';
            const hour12 = h24 % 12 || 12;
            return { date, ampm, hour12, minute: m };
        },
        todayStr() {
            const now = new Date();
            const y   = now.getFullYear();
            const mo  = String(now.getMonth() + 1).padStart(2, '0');
            const d   = String(now.getDate()).padStart(2, '0');
            return `${y}-${mo}-${d}`;
        },
        nowAmpm() {
            return new Date().getHours() >= 12 ? 'pm' : 'am';
        },
        nowHour12() {
            const h = new Date().getHours();
            return h % 12 || 12;
        },
        nowMinute() {
            return Math.floor(new Date().getMinutes() / 10) * 10;
        },
        setToday() {
            this.innerDate   = this.todayStr();
            this.innerAmpm   = this.nowAmpm();
            this.innerHour12 = this.nowHour12();
            this.innerMinute = this.nowMinute();
        },
        onDateSelected() {
            this.minuteOpen = false;
        },
        selectMinute(m) {
            this.innerMinute = m;
            this.minuteOpen  = false;
        },
        confirm() {
            const h24 = this.innerAmpm === 'pm'
                ? (this.innerHour12 === 12 ? 12 : this.innerHour12 + 12)
                : (this.innerHour12 === 12 ? 0  : this.innerHour12);
            const val = `${this.innerDate || this.todayStr()} ${String(h24).padStart(2, '0')}:${String(this.innerMinute).padStart(2, '0')}`;
            this.$emit('input', val);
            this.menu = false;
        },
    },
};
</script>

<style scoped>
.app-dtp-card {
    padding-bottom: 0;
}

/* 오늘 버튼 행 */
.app-dtp-today-row {
    display: flex;
    justify-content: flex-end;
    padding: 6px 8px 0;
}
.app-dtp-today-btn {
    font-size: 13px !important;
    border: 1px solid #d0d0d0;
    border-radius: 6px !important;
    padding: 0 10px !important;
    height: 28px !important;
    min-width: 0 !important;
}

/* 달력 */
.app-dtp-datepicker {
    border-bottom: none !important;
    box-shadow: none !important;
}

/* 분 오버레이 */
.app-dtp-minute-overlay {
    position: absolute;
    right: 0;
    top: 44px;
    width: 88px;
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    z-index: 200;
    overflow-y: auto;
    max-height: 216px;
}
.app-dtp-minute-item {
    padding: 10px 16px;
    cursor: pointer;
    font-size: 14px;
    color: #333;
    transition: background 0.1s;
}
.app-dtp-minute-item:hover {
    background: #f5f5f5;
}
.app-dtp-minute-item--active {
    color: #1976D2;
    font-weight: bold;
}

/* 시간 선택 행 */
.app-dtp-time-row {
    display: flex;
    gap: 8px;
    padding: 8px 12px;
}
.app-dtp-select {
    flex: 1;
    border: 1px solid #bdbdbd;
    border-radius: 6px;
    padding: 7px 8px;
    font-size: 14px;
    background: #fff;
    outline: none;
    cursor: pointer;
    min-width: 0;
}
.app-dtp-select:focus {
    border-color: #1976D2;
}
.app-dtp-minute-trigger {
    flex: 1;
    border: 1px solid #bdbdbd;
    border-radius: 6px;
    padding: 7px 8px;
    font-size: 14px;
    background: #fff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-width: 0;
    user-select: none;
}
.app-dtp-minute-trigger:hover {
    border-color: #1976D2;
}

/* 확인 버튼 행 */
.app-dtp-confirm-row {
    padding: 4px 12px 12px;
}
</style>
